import { assert } from '@fe/core';
import { GDFPalletCode } from '../../gdf/type';
import { getPalette } from '../../palettes/palettes';
import { WeatherLightningSymbol, WeatherSettingsChangeSource } from '../../settings/settings';
import { generateRampFromPalette } from '../../utils/ramp';
import { MessageType } from '../../worker/actor/actor-messages';
import { SymbolLayer2 } from '../custom/symbol2/symbol-layer2';
import { WeatherLayerBase } from '../weather-layer-base';

export class LightningLayer extends WeatherLayerBase {
    private symbolLayer: SymbolLayer2 | null = null;
    private paletteId: GDFPalletCode = GDFPalletCode.none;

    protected async loadFrame(frameId: number): Promise<void> {
        const data = await this.dispatcher.dispatch({
            type: MessageType.getLightningPoints,
            data: {
                id: this.productId,
                frames: [frameId],
                workspaceId: this.workspaceId
            }
        });
        const d = data?.[0];
        if (d) {
            this.paletteId = d.palette;
            this.symbolLayer!.size = (z) => {
                if (z < 3) {
                    return 7;
                }
                return 9;
            };
            this.symbolLayer!.setData(d.points, d.iconColor);
            this.loadSettings();
        }
    }

    protected async loadProduct(): Promise<void> {}

    protected onAdd(): void {
        this.symbolLayer = new SymbolLayer2(this.baseId);
        this.map.addLayer(this.symbolLayer, this.slotTopLayerId);
    }

    protected onRemove(): void {
        assert(this.symbolLayer);
        this.map.removeLayer(this.symbolLayer.id);
    }

    protected onDisplayChanged(): void {
        if (this.symbolLayer) {
            this.map.setLayoutProperty(this.symbolLayer.id, 'visibility', this.shouldDisplay() ? 'visible' : 'none');
        }
    }

    protected onSettingsUpdated(changeSource: WeatherSettingsChangeSource): void {
        if (changeSource !== WeatherSettingsChangeSource.lightning) {
            return;
        }
        this.loadSettings();
    }

    private loadSettings() {
        assert(this.symbolLayer);
        const ramp = generateRampFromPalette(getPalette(this.paletteId, this.settings)!);
        this.symbolLayer.setColorRamp(ramp);
        this.symbolLayer.setImages(symbols[this.settings.lightning.symbol]);
        this.symbolLayer.size = sizes[this.settings.lightning.symbol];
    }
}

const symbols: Record<WeatherLightningSymbol, string[]> = {
    [WeatherLightningSymbol.dot]: ['fe-weather:circle-filled', 'fe-weather:circle-filled'],
    [WeatherLightningSymbol.plus]: ['fe-weather:plus', 'fe-weather:plus'],
    [WeatherLightningSymbol.sign]: ['fe-weather:plus', 'fe-weather:minus'],
    [WeatherLightningSymbol.icon]: ['fe-weather:lightning', 'fe-weather:lightning']
};

const sizes: Record<WeatherLightningSymbol, number> = {
    [WeatherLightningSymbol.dot]: 10,
    [WeatherLightningSymbol.plus]: 10,
    [WeatherLightningSymbol.sign]: 10,
    [WeatherLightningSymbol.icon]: 16
};
